import { vec3 } from 'gl-matrix';
import { boundsCornersMm } from '~/domain/services/imageService/gridGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { VoiBounds } from '~/state/atoms/toolsAtoms';

/** CSS-pixel rectangle in a viewport's canvas coordinate space. */
export interface CanvasRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Fallback for {@link voiOnSlabToleranceMm} when no image grid is
 * available to derive the slice spacing from: at least half the largest
 * expected slice spacing (typical CT spacing: 1–5 mm).
 */
const FALLBACK_ON_SLAB_TOLERANCE_MM = 2.5;

/**
 * Screen-space rectangle of a world-mm VOI box in `view`: ALL 8 corners
 * are projected and their per-axis extremes taken — through the shared
 * {@link boundsCornersMm}, the same enumeration {@link gridSubRange}
 * resolves the box to voxels with — so the drawn rect is the exact
 * screen footprint of the box under ANY view
 * basis (axis flips from a non-HFS patient position, and rotated
 * frames). Pure view math, extracted from the overlay component so the
 * cross-view consistency of the drawn rects is directly testable: two
 * views sharing a world axis must show the box's interval on that axis
 * identically, because each rect edge maps back to a bounds face.
 */
export function projectVoiBoundsToCanvasRect(
    view: PlaneView,
    bounds: VoiBounds
): CanvasRect {
    const projected = boundsCornersMm(bounds).map((corner) =>
        view.canvasCoordsFromPatientCoords(corner)
    );

    const xs = projected.map((p) => p[0]);
    const ys = projected.map((p) => p[1]);

    const left = Math.min(...xs);
    const top = Math.min(...ys);
    return {
        left,
        top,
        width: Math.max(...xs) - left,
        height: Math.max(...ys) - top,
    };
}

/**
 * World-mm interval the box occupies along `view`'s normal, as the
 * extremes of the 8 corners' projections onto {@link
 * PlaneView.viewDirection}. For the box's axis-aligned bounds this is
 * exact under ANY view basis — flipped patient positions and rotated
 * frames included — because a linear projection of a convex hull attains
 * its extremes at the hull's own corners. The interval is reported low
 * to high regardless of the normal's sign, so callers compare {@link
 * PlaneView.planeCoordinates} (a projection along the SAME normal)
 * against it directly.
 */
export function voiIntervalAlongViewNormal(
    view: PlaneView,
    bounds: VoiBounds
): [number, number] {
    const projections = boundsCornersMm(bounds).map((corner) =>
        vec3.dot(corner, view.viewDirection)
    );
    return [Math.min(...projections), Math.max(...projections)];
}

/**
 * Half the through-plane slice spacing of the displayed image, in mm —
 * the same derivation as the measurement overlay's on-plane tolerance,
 * and for the same reason: the displayed slice REPRESENTS a
 * half-spacing-thick slab either side of its plane, so the box is "on
 * this slice" exactly when its extent reaches into that slab. Half a
 * spacing (not a fixed constant) keeps a box thinner than one slice
 * visible on the slice nearest to it while never lighting up a
 * neighbouring slice whose slab the box does not touch. Falls back to
 * {@link FALLBACK_ON_SLAB_TOLERANCE_MM} when no grid is available.
 *
 * COORDINATE FRAMES: `view.viewDirection` is a unit normal in the
 * PATIENT frame, while `grid.getRes()` is indexed by the GRID's own
 * axes, so the normal is rotated into the grid frame before it is
 * combined with the per-axis spacing. Dotting the two directly (as this
 * did) silently assumed grid axes == patient axes: correct for the four
 * transversal patient positions, wrong on any coronal- or sagittal-ACQUIRED
 * series, where it read the tolerance off the wrong axis (on a
 * 1.42 × 1.42 × 2.03 mm coronal-acquired PET the stack-direction view
 * got 0.71 mm instead of 1.015 mm, hiding the box on the slices at the
 * ends of its own extent).
 *
 * The combination is the voxel box's extent ALONG the normal
 * (sum of |n_i| · res_i in the grid frame). For every signed-permutation
 * orientation — the four patient positions and the 90° acquisition
 * rotations — the rotated normal is exactly ±e_k, so this is exactly
 * res[k], the spacing of the axis the view scrolls along. On a genuinely
 * oblique grid no single axis is "the" through-plane one; the voxel's
 * extent along the normal is the conservative reading (never narrower
 * than any per-axis spacing), which keeps a thin box visible rather than
 * dropping it off the slab.
 */
export function voiOnSlabToleranceMm(view: PlaneView): number {
    const image = view.environment.image;
    if (!image) {
        return FALLBACK_ON_SLAB_TOLERANCE_MM;
    }
    const grid = image.grid;
    const normalInGrid = grid.rotateToGrid(view.viewDirection);
    const resMm = grid.getRes();
    const throughPlaneMm =
        Math.abs(normalInGrid[0]) * resMm[0] +
        Math.abs(normalInGrid[1]) * resMm[1] +
        Math.abs(normalInGrid[2]) * resMm[2];
    return throughPlaneMm > 0
        ? throughPlaneMm / 2
        : FALLBACK_ON_SLAB_TOLERANCE_MM;
}

/**
 * Relative size of the inscribed ellipsoid's cross-section on `view`'s
 * current plane, in `[0, 1]`: 1 through the centre, shrinking to 0 at the
 * poles, as `sqrt(1 - d²)` for the plane's normalized offset `d` from the
 * centre along the view normal.
 *
 * The overlay needs this because an ellipsoid drawn at full width on
 * every slice would claim a reach the region does not have there — the
 * user would read a peripheral slice as restricted far wider than the
 * segmentation can actually go. A box has no such gap between its
 * footprint and its cross-section, which is why it needs no equivalent.
 *
 * Exact when the view normal is one of the region's own axes, which is
 * every orthogonal plane view on an axis-aligned grid. On a genuinely
 * rotated frame it reads off the corner-projected interval, the same
 * over-covering approximation {@link projectVoiBoundsToCanvasRect} draws
 * the footprint with.
 */
export function ellipsoidCrossSectionScale(
    view: PlaneView,
    bounds: VoiBounds
): number {
    const [low, high] = voiIntervalAlongViewNormal(view, bounds);
    const half = (high - low) / 2;
    if (half <= 0) {
        return 1;
    }
    const offset = (view.planeCoordinates - (low + high) / 2) / half;
    return Math.sqrt(Math.max(0, 1 - offset * offset));
}

/**
 * Whether `view`'s current plane cuts the box's slab: the plane
 * coordinate must lie within the box's normal-axis interval, widened by
 * {@link voiOnSlabToleranceMm} on each end (inclusive). This is the test
 * the overlay's visibility rule is built on — a box whose extent along
 * this view's normal lies elsewhere in the patient is NOT on the
 * displayed slice, and drawing it interactive there misreads as a
 * restriction of the visible anatomy.
 */
export function isVoiBoxOnCurrentSlice(
    view: PlaneView,
    bounds: VoiBounds
): boolean {
    const [low, high] = voiIntervalAlongViewNormal(view, bounds);
    const toleranceMm = voiOnSlabToleranceMm(view);
    const planeCoordinateMm = view.planeCoordinates;
    return (
        planeCoordinateMm >= low - toleranceMm &&
        planeCoordinateMm <= high + toleranceMm
    );
}
